import { useDuplicatePlaybookMutation } from '@carboncopyco/data';
import type { Playbook } from '@carboncopyco/types';
import { ConfirmAlert, notify } from '@carboncopyco/ui';
import { AnalyticsTrackEventName, useAnalytics } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';

type DuplicatePlaybookModalProps = {
  onClose: () => void;
  show: boolean;
  organizationId: string;
  workspaceId: string;
  playbookId: string;
  goToDuplicatedPlaybook?: (playbookId: Playbook['id']) => void;
};

export const DuplicatePlaybookModal = ({
  onClose,
  show,
  organizationId,
  playbookId,
  workspaceId,
  goToDuplicatedPlaybook,
}: DuplicatePlaybookModalProps) => {
  const [duplicatePlaybook, { isLoading }] = useDuplicatePlaybookMutation();
  const { trackEvent } = useAnalytics();
  const [isDisabled, setIsDisabled] = useBoolean();
  const location = 'demo_info_page_duplicate_demo_modal';

  return (
    <ConfirmAlert
      show={show}
      title="Duplicate this playbook?"
      containerClass="w-ds-normal-modal"
      description="A new playbook will be created as a copy of this one"
      noSecondButton
      onConfirm={() => {
        setIsDisabled.setTrue();
        trackEvent(AnalyticsTrackEventName.DEMO_CLICK_DUPLICATE_APPROVE, {
          location,
        });
        duplicatePlaybook({
          organizationId,
          workspaceId,
          id: playbookId,
        })
          .unwrap()
          .then(({ id }) => {
            goToDuplicatedPlaybook?.(id);
          })
          .catch(() => {
            notify.error('Error duplicating playbook');
          })
          .finally(() => {
            setIsDisabled.setFalse();
            onClose();
          });
      }}
      yesLabel="Duplicate"
      onClose={() => {
        trackEvent(AnalyticsTrackEventName.DEMO_CLICK_DUPLICATE_CANCEL, {
          location,
        });
        onClose();
      }}
      loading={isLoading}
      disabled={isDisabled}
    />
  );
};
